import { useIonToast } from '@ionic/react';
import { useIntl } from 'react-intl';
import useGravityForm, { useGFormsSubmit } from '../../hooks/GFormsHooks';
import {
  GF_Confirmation,
  GF_Field,
  GF_FieldError,
  GF_Form,
} from '../../model/gforms/gform';
import GravityFormsField from './GFormsField';

interface Props {
  form: GF_Form;
}

export default function GFormsForm({ form }: Props) {
  const [present] = useIonToast();
  const intl = useIntl();

  const { submitForm, result, loading } = useGFormsSubmit();
  const haveFieldErrors = Boolean(
    result?.validation_messages &&
      Object.entries(result.validation_messages).length > 0
  );
  const defaultConfirmation: GF_Confirmation | undefined = form.confirmations
    ? Object.entries(form.confirmations)
        .filter(([key, confirmation]) => confirmation?.isDefault)
        .map(([key, confirmation]) => confirmation)
        .pop()
    : undefined;
  const formFields = form.fields || [];
  const { state } = useGravityForm();

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (loading) return;

    submitForm({
      formId: form.id,
      fieldValues: state,
    })
      .then((formResult) => {
        if (formResult?.is_valid === true) {
          present({
            color: 'primary',
            position: 'top',
            message:
              defaultConfirmation?.message ||
              intl.formatMessage({
                id: 'Form well submitted',
              }),
            duration: 2000,
          });
        } else if (
          Object.keys(formResult?.validation_messages || {}).length === 0
        ) {
          present({
            color: 'dark',
            position: 'top',
            message: intl.formatMessage({
              id: 'Form unknown error',
            }),
            duration: 2000,
          });
        }
      })
      .catch((error) => {
        present({
          color: 'dark',
          position: 'top',
          message: error.message,
          duration: 2000,
        });
        console.error(error);
      });
  }

  function getFieldErrors(id: number): GF_FieldError[] {
    if (!haveFieldErrors) return [];
    return Object.entries(result?.validation_messages || {})
      .map(([inputId, message]) => ({ id: inputId, message: message }))
      .filter((error: GF_FieldError) => '' + error.id === '' + id);
  }

  return (
    <form className="px-5 pb-10" method="post" onSubmit={handleSubmit}>
      {form.title && (
        <h2 className="font-bold font-sans text-lg text-center">
          {form.title}
        </h2>
      )}
      {form.description && (
        <p className="text-center text-base font-sans text-black mt-5">
          {form.description}
        </p>
      )}
      <div className="mt-8">
        {formFields.map((field) => (
          <GravityFormsField
            key={field?.id}
            field={field as GF_Field}
            fieldErrors={getFieldErrors(Number(field?.id))}
          />
        ))}
      </div>
      <div className="flex justify-center w-full">
        <button
          className="inline-flex items-center justify-center px-6 py-4 mt-3 mb-3 w-1/2 border border-transparent shadow-sm font-bold rounded-3xl text-sm text-white text-center bg-primary hover:bg-secondary focus:outline-none disabled:bg-lightGrey"
          type="submit"
          disabled={loading}
        >
          {form?.button?.text || 'Submit'}
        </button>
      </div>
    </form>
  );
}
